import { ref, computed, watch, toValue, type MaybeRefOrGetter, type Ref } from 'vue';
import useSwr from '@/core/composables/swr/useSwr';
import { PaginatedData } from '@/core/interfaces/api';

/**
 * Options for configuring infinite loading behavior
 */
interface UseInfiniteLoadingOptions {
    /** Number of items to load per page. Defaults to 20 */
    limit?: number;
    /** Initial page number to start loading from. Defaults to 1 */
    initialPage?: number;
}

/**
 * Return type for the useInfiniteLoading composable
 */
interface UseInfiniteLoadingReturn<T> {
    /** Accumulated data from all loaded pages */
    data: Ref<T[]>;
    /** Whether the initial page is currently loading */
    isLoading: Ref<boolean>;
    /** Whether additional pages are currently being loaded */
    isLoadingMore: Ref<boolean>;
    /** Error from the last request, if any */
    error: Ref<Error | undefined>;
    /** Whether more data is available to load */
    hasMore: Ref<boolean>;
    /** Total number of items available */
    total: Ref<number>;
    /** Function to load the next page of data */
    loadMore: () => void;
    /** Function to refresh all data from the beginning */
    refresh: () => Promise<void>;
}

/**
 * Composable for implementing infinite loading with SWR caching
 *
 * Provides infinite scroll functionality by managing paginated API requests,
 * accumulating data across pages, and preventing duplicate loads. Integrates
 * with SWR for caching and automatic revalidation.
 *
 * @example
 * ```ts
 * const { data, isLoading, loadMore, hasMore } = useInfiniteLoading(
 *   'users-list',
 *   (page, limit) => api.getUsers({ page, limit }),
 *   { limit: 20 }
 * );
 * ```
 *
 * @param keyPrefix - Unique prefix for SWR cache keys; when it changes (a filter), the list starts over
 * @param fetcher - Function that fetches paginated data
 * @param options - Configuration options for infinite loading behavior
 * @returns Object containing data, loading states, and control functions
 */
export default function useInfiniteLoading<T>(
    keyPrefix: MaybeRefOrGetter<string>,
    fetcher: (page: number, limit: number) => Promise<PaginatedData<T[]>>,
    options: UseInfiniteLoadingOptions = {}
): UseInfiniteLoadingReturn<T> {
    const { limit = 20, initialPage = 1 } = options;

    const currentPage = ref(initialPage);
    // One slot per loaded page: SWR answers a page twice when it serves it from
    // cache then revalidates it, and the second answer must replace, not append.
    const pages = ref<T[][]>([]) as Ref<T[][]>;
    const allData = computed(() => pages.value.flat() as T[]);
    const total = ref(0);
    const isLoadingMore = ref(false);
    const hasLoadedInitial = ref(false);

    const prefix = computed(() => toValue(keyPrefix));

    // Synchronous, so the SWR key never pairs the new prefix with the old page.
    watch(prefix, () => {
        currentPage.value = initialPage;
        pages.value = [];
        total.value = 0;
        hasLoadedInitial.value = false;
        isLoadingMore.value = false;
    }, { flush: 'sync' });

    const key = computed(() => `${prefix.value}-page-${currentPage.value}-limit-${limit}`);

    const {
        data: currentPageData,
        error,
        isLoading: isLoadingCurrentPage,
        mutate
    } = useSwr(key, () => fetcher(currentPage.value, limit));

    watch(currentPageData, (newData) => {
        if (newData) {
            // The pages after this one belong to an older load (back on the first page).
            const index = currentPage.value - initialPage;
            pages.value = [...pages.value.slice(0, index), [...newData.data]];

            if (index === 0) {
                hasLoadedInitial.value = true;
            }

            total.value = newData.meta?.total || 0;
            isLoadingMore.value = false;
        }
    }, { immediate: true });

    watch(error, (newError) => {
        if (newError) {
            isLoadingMore.value = false;
        }
    });

    // Loading until the first page of the current prefix arrives: after a prefix
    // change SWR still holds the previous key's data, so its own loading flag is off.
    const isLoading = computed(() => {
        return !hasLoadedInitial.value && !error.value;
    });

    const hasMore = computed(() => {
        if (!hasLoadedInitial.value || total.value === 0) return false;
        return allData.value.length < total.value;
    });

    const loadMore = (): void => {
        if (!hasMore.value || isLoadingMore.value || isLoadingCurrentPage.value) return;

        isLoadingMore.value = true;
        currentPage.value += 1;
    };

    const refresh = async (): Promise<void> => {
        currentPage.value = initialPage;
        pages.value = [];
        total.value = 0;
        hasLoadedInitial.value = false;
        isLoadingMore.value = false;

        await mutate();
    };

    return {
        data: allData,
        isLoading,
        isLoadingMore: computed(() => isLoadingMore.value),
        error,
        hasMore,
        total,
        loadMore,
        refresh
    };
}
